import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Json2csvService } from 'csvtools-lib';
import { Subscription } from 'rxjs';
import { Breadcrumb } from 'src/app/models/breadcrumb-models/breadcrumb-model/breadcrumb.model';
import { BRoute } from 'src/app/models/breadcrumb-models/breadcrumb-route-model/breadcrumb-route.model';
import { LicenseDevice } from 'src/app/models/licenses-model/license-device.model';
import { License } from 'src/app/models/licenses-model/license.model';
import { DateUnixTransformerPipe } from 'src/app/pipes/date-unix-transformer-pipe/date-unix-transformer.pipe';
import { LicensesService } from 'src/app/services/licenses-service/licenses.service';
import { HeaderBreadcrumbService } from '../../header-components/header-breadcrumb/header-breadcrumb.service';
import { SnackBarService } from '../../shared-components/snack-bar/snack-bar.service';

@Component({
  selector: 'app-licenses-licenses-dashboard',
  templateUrl: './licenses-licenses-dashboard.component.html',
  styleUrls: ['./licenses-licenses-dashboard.component.scss']
})
export class LicensesLicensesDashboardComponent implements OnInit, OnDestroy {

  licenses: License[];
  filteredLicenses: License[];
  filterOpts;
  filterKey;
  noItemsConfig;
  getLicensesSubscription: Subscription;
  getLicensesSubscriptionRefresh: Subscription;
  filterLicensesSubscription: Subscription;
  searchLicensesSubscription: Subscription;
  exportLicensesSubscription: Subscription;

  constructor(
    private headerBreadcrumbService: HeaderBreadcrumbService,
    private licensesService: LicensesService,
    private json2csvService: Json2csvService,
    private dateUnixTransformerPipe: DateUnixTransformerPipe,
    private snackbarService: SnackBarService,
    private cd: ChangeDetectorRef
  ) {
    this.noItemsConfig = this.setNoItemsConfig();
  }

  ngOnInit() {
    this.setBreadcrumb();
    this.getLicenses();
    this.filterLicensesSubscription = this.licensesService.filterLicensesListen().subscribe(
      (res) => {
        this.filterOpts = res;
        this.applyFilters();
        this.cd.detectChanges();
      }
    );
    this.searchLicensesSubscription = this.licensesService.searchLicensesListen().subscribe(
      (res) => {
        this.filterKey = res;
        this.applyFilters();
      }
    );
    this.exportLicensesSubscription = this.licensesService.exportLicensesListen().subscribe(
      () => this.exportLicensesList()
    );
  }

  public getLicenses() {
    this.getLicensesSubscription = this.licensesService.getLicenses().subscribe(
      (res) => {
        this.licenses = res;
        this.applyFilters();
      },
      (error) => {
        this.snackbarService.open(error['error']['message'], 'error');
      }
    );
  }

  public exportLicensesList() {
    this.applyFilters();
    const data = [];
    (this.filteredLicenses || []).forEach((license: License) => {
      const devices = license.DeviceList && license.DeviceList.length > 0 ? license.DeviceList : [null];
      devices.forEach((device: LicenseDevice) => {
        data.push([
          license.Description,
          license.Quantity,
          license.InUse,
          this.formatDate(license.StartDate, 'false'),
          this.formatDate(license.EndDate, 'false'),
          this.booleanToCsv(license.MultiCenter),
          this.booleanToCsv(license.MultiApp),
          license.CenterName,
          license.AppName,
          device ? device.DeviceName : '',
          device ? device.DeviceTypeName : '',
          device ? device.DeviceModel : '',
          device ? device.CenterName : '',
          device ? this.formatDate(device.LastConnection) : ''
        ]);
      });
    });
    if (data.length === 0) {
      this.snackbarService.open('licenses-page.licenses not found', 'warning');
      return;
    }
    this.json2csvService.setBody(data, ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13']);
    this.json2csvService.setHeader(['License', 'Quantity', 'In use', 'Start date', 'End date', 'Multi center', 'Multi app', 'License center', 'Application', 'Device', 'Device type', 'Device model', 'Device center', 'Device last connection']);
    this.json2csvService.generateCSV('licenseList');
  }

  private applyFilters() {
    if (!this.licenses) {
      this.filteredLicenses = this.licenses;
      return;
    }
    this.filteredLicenses = this.licenses.filter((license: License) => this.matchesSearch(license) && this.matchesFilters(license));
  }

  private matchesSearch(license: License) {
    if (!this.filterKey) {
      return true;
    }
    const description = license.Description ? license.Description : '';
    return description.toLowerCase().indexOf(this.filterKey.toLowerCase()) !== -1;
  }

  private matchesFilters(license: License) {
    if (!this.filterOpts) {
      return true;
    }
    return this.matchesCenter(license, this.filterOpts.centers) &&
      this.matchesDeviceField(license, this.filterOpts.types, 'type') &&
      this.matchesDeviceField(license, this.filterOpts.models, 'model') &&
      this.matchesLicenseField(license.AppName, this.filterOpts.apps);
  }

  private matchesCenter(license: License, centers: string[]) {
    if (!centers || centers.length === 0) {
      return true;
    }
    if (centers.indexOf(license.CenterName) !== -1) {
      return true;
    }
    return (license.DeviceList || []).some((device: LicenseDevice) => centers.indexOf(device.CenterName) !== -1);
  }

  private matchesDeviceField(license: License, values: string[], field: string) {
    if (!values || values.length === 0) {
      return true;
    }
    return (license.DeviceList || []).some((device: LicenseDevice) => values.indexOf(this.getDeviceField(device, field)) !== -1);
  }

  private getDeviceField(device: LicenseDevice, field: string) {
    switch (field) {
      case 'type':
        return device.DeviceTypeName;
      case 'model':
        return device.DeviceModel;
      default:
        return '';
    }
  }

  private matchesLicenseField(value: string, values: string[]) {
    if (!values || values.length === 0) {
      return true;
    }
    return values.indexOf(value) !== -1;
  }

  private formatDate(value, args?) {
    return value ? this.dateUnixTransformerPipe.transform(value, args) : '';
  }

  private booleanToCsv(value: boolean) {
    return value === true ? 'true' : 'false';
  }

  private setBreadcrumb() {
    this.headerBreadcrumbService.setRoutes(new Breadcrumb('fas fa-file-code', 'licenses-page.licenses', '/account', [new BRoute('account-page.account', '/account'), new BRoute('licenses-page.licenses')]));
  }

  private setNoItemsConfig() {
    return {
      description: 'roles-page.roles not found',
      action: '',
      lottieConfig: {
        path: 'assets/lottie-animations/empty-list.json',
        renderer: 'canvas',
        autoplay: true,
        loop: true
      }
    };
  }

  ngOnDestroy() {
    if (this.getLicensesSubscription) { this.getLicensesSubscription.unsubscribe(); }
    if (this.filterLicensesSubscription) { this.filterLicensesSubscription.unsubscribe(); }
    if (this.searchLicensesSubscription) { this.searchLicensesSubscription.unsubscribe(); }
    if (this.exportLicensesSubscription) { this.exportLicensesSubscription.unsubscribe(); }
  }

}
